import type { Contract } from "../types/contract";

/** Passed via React Router `location.state` when navigating from the contract list after opening the host file. */
export type ReviewPageNavigationState = {
  readonly hostDocumentOpenedFromList?: boolean;
};

function normalizeUuidToken(value: string | null | undefined): string | null {
  const t = (value ?? "").trim().toLowerCase();
  return t.length > 0 ? t : null;
}

/** True when a reviewer has been assigned (submit-for-review handoff completed). */
export function isContractSubmittedToReviewer(contract: Contract): boolean {
  return normalizeUuidToken(contract.reviewerUserId) != null;
}

/**
 * True when the current user is the contract uploader/creator (`created_by`).
 * When `createdBy` is absent on the contract row, returns false (safe default).
 */
export function isCurrentUserContractUploader(
  contract: Contract,
  currentUserId: string | null,
): boolean {
  const owner = normalizeUuidToken(contract.createdBy);
  const user = normalizeUuidToken(currentUserId);
  if (owner == null || user == null) {
    return false;
  }
  return owner === user;
}

export type SubmitForReviewSummaryOptions = {
  /**
   * True when the user opened this review after choosing the contract from **contracts** (list path).
   * FR-008: hide Submit/Send for review during active list-driven reviewer phase.
   */
  readonly openedFromContractList?: boolean;
};

/**
 * Submit-for-review on the review summary is disabled for all users (product policy; spec 034).
 * Signature retained for call sites.
 */
/* eslint-disable @typescript-eslint/no-unused-vars -- parameters kept for stable call signature */
export function shouldShowSubmitForReviewOnSummary(
  _contract: Contract,
  _options?: SubmitForReviewSummaryOptions,
): boolean {
  return false;
}
/* eslint-enable @typescript-eslint/no-unused-vars */

/** Completed reviews are view-only on the summary (no workflow mutations through these controls). */
export function isContractReviewReadOnly(contract: Contract): boolean {
  return contract.status === "Completed";
}

/**
 * Hide the AI suggestions panel while the contract is not ready for suggestion review.
 */
export function shouldSuppressAiSuggestionsPanel(contract: Contract): boolean {
  const { status } = contract;
  return (
    status === "processing" ||
    status === "processing_failed" ||
    status === "ai_in_progress"
  );
}

